import type { ReactElement } from 'react'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { BulletedList } from '@/components/layouts/Ir/BulletedList'
import { BHeading } from '@/components/layouts/Ir/Heading'
import { Paragraph } from '@/components/layouts/Ir/Paragraph'

export const IssuePage = () => {
  return (
    <Layout>
      <SectionTitle
        color="normal"
        titleText="Issues"
        subTitleText="対処すべき課題"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <Paragraph>
          当社グループは、流通小売事業においては、全国に店舗網を拡大するとともに、生鮮を中心とした「食」の強化や、店舗改装などによって店舗の集客力及び収益性を高めてまいります。また、リテールAI事業においては、プロダクトやソリューションの新規開発や既存プロダクトの機能改善により、小売企業、食品・消費財メーカー、ベンダー等への更なる提供価値の向上に努めてまいります。エネルギー価格や人件費の上昇による物価高に起因して消費者の節約志向が高まるなど、厳しい経営環境が続く状況にありますが、中長期的な成長を見据えた事業基盤を築いてまいります。
        </Paragraph>
        <Paragraph>
          当社グループは、2025年7月1日付の西友の完全子会社化により、事業規模及び事業基盤が大きく拡大しました。一方、両社の経営資源の融合、グループシナジーの早期実現、既存事業の収益力向上、財務健全性の確保及び拡大した企業集団に対応する経営管理体制の構築が、重要な経営課題となっております。
        </Paragraph>
        <Paragraph>
          こうした認識のもと、当社グループは、2026年2月に中期経営計画を策定し、2027年6月期から2029年6月期までの3年間を、長期ビジョンの実現のための「流通改革に向けた基盤づくりの3年」と位置づけています。
        </Paragraph>
        <Paragraph>
          2029年6月期における定量目標として、売上高1.6兆円、EBITDA1,000億円、営業利益640億円を掲げております。シナジーを軸としたキャッシュ創出力を向上させることで、財務健全性を高めるとともに、2030年6月期以降の飛躍的な成長に向けた、強固な土台づくりに取り組んでまいります。
        </Paragraph>
        <Paragraph>
          これらを達成するため、当社グループは以下の5点を重点戦略として設定し、優先的に対処してまいります。
        </Paragraph>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>① PMI推進によるグループシナジー最大化</BHeading>
        <Paragraph>
          西友との統合プロセスを迅速かつ着実に実行し、グループ全体の収益性と生産性の向上を図ってまいります。
        </Paragraph>
        <BulletedList>
          <li>仕入れ・調達の最適化：仕入れ条件の統一及び帳合統合を進め、粗利押し上げ効果を創出いたします。</li>
          <li>PB強化とカテゴリマネジメントの高度化：「こはく本舗」や「みなさまのお墨付き」等の両社ブランドの再編・相互販売・共同開発を推進し、グループ全体のPB売上高構成比25％を目指します。併せて、AIやデータ分析による棚効率改善、プロセスセンターやセントラルキッチンの運用一体化を進めてまいります。</li>
          <li>システム統合の推進：西友の基幹システムをトライアルのシステムへ統合・一本化し、開発・運用・保守コストの削減を図ってまいります。</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>② トライアルの収益性向上と成長の推進</BHeading>
        <Paragraph>
          高収益モデルの磨き上げと戦略的な店舗展開により、持続的な成長を牽引してまいります。
        </Paragraph>
        <BulletedList>
          <li>出店戦略の加速：独自の商圏分析ツール「Retail Map」を活用した精度の高い出店により、主力のスーパーセンターを3年間で35店舗出店するほか、小型店フォーマット「TRIAL GO」の多店舗展開（3年間で100店舗）を進めてまいります。</li>
          <li>既存店売上高の継続的成長：積極的な店舗改装（3年間で累計45店舗）や「食」・PB商品の強化（惣菜売上構成比8％目標）を実施いたします。</li>
          <li>デジタルを活用した購買体験の向上：プライシングの精緻化に加え、Skip Cartやインストアサイネージ、会員アプリ等をフル活用した個客最適のアプローチを行い、「価格訴求」と「価値訴求」の両立を実現いたします。</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>③ 西友の店舗改革とお客様支持の拡大</BHeading>
        <Paragraph>
          西友における抜本的な店舗改革を断行し、トップラインの伸長と圧倒的な顧客支持を獲得してまいります。
        </Paragraph>
        <BulletedList>
          <li>売場づくりと改装の横展開：成功モデル店舗のノウハウを全店へ横展開し、店舗の底上げを図るとともに、スーパーマーケット60店舗の戦略的改装を実施いたします。</li>
          <li>新業態「トライアル西友」への展開：大型店（ハイパーマーケット）30店舗を、価格競争力を強みとする生鮮繁盛店モデル「トライアル西友」へと業態転換いたします。</li>
          <li>販管費率の低減：攻めの価格投資により一時的に売上総利益率は低下するものの、売上高の拡大による固定費の吸収とシステム統合等の効果により、大幅な販管費率の低減と増益体制を構築してまいります。</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>④ DX戦略の収益化フェーズへの移行</BHeading>
        <Paragraph>
          これまで構築してきたDX基盤を本格的な収益化へと移行させ、グループ全体の成長を後押ししてまいります。
        </Paragraph>
        <BulletedList>
          <li>データとAIの全社活用：基幹システムの統合に合わせ、西友も含めたグループ全社でデータ及びAIの活用を推進いたします。</li>
          <li>新規事業基盤の構築：リテールメディアやECの事業基盤づくりを具体的に進め、将来の売上高・利益拡大に寄与する体制を確立してまいります。</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>⑤ 持続的な成長を支える経営基盤の強化</BHeading>
        <Paragraph>
          成長投資と財務健全性のバランスを高度に両立させ、企業価値の最大化を図ってまいります。
        </Paragraph>
        <BulletedList>
          <li>サステナビリティ経営の推進：当社グループのパーパスである『世界の誰もが「豊かさ」を享受できる社会をつくる。』のもと、当社グループ及び社会全体の発展・成長に取り組んでまいります。リアル店舗の運営を軸に、リテールDXの推進や、多様なパートナーとの連携を通じて持続可能な社会の実現を目指してまいります。</li>
          <li>適切なキャッシュアロケーション：営業活動から創出されるキャッシュを主財源として、成長投資（新店出店・業態転換・店舗改装等）へ1,300億円を優先配分いたします。</li>
          <li>財務基盤の正常化：西友の完全子会社化に伴う有利子負債の圧縮（3年間で1,150億円の返済）を規律をもって実行し、2029年6月期における「純有利子負債／EBITDA倍率3倍以内」「自己資本比率20％以上」の達成を目指します。</li>
          <li>人的資本・パートナーシップ・株主還元：人的資本の強化や業界変革につながる外部パートナーシップの構築を進めるほか、機動的なM&Aも検討してまいります。また、株主の皆様へは「累進配当」の方針に基づき、安定的かつ継続的な還元を行ってまいります。</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          当社グループは、これらの重点戦略を全社一丸となって推進し、次世代スマートストアの成功モデルを確立・横展開することで、世界をリードする「流通プラットフォーマー」への飛躍を目指してまいります。
        </Paragraph>
      </section>
    </Layout>
  )
}

IssuePage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '/ir' },
    { name: '経営方針', path: '/ir/manage' },
    { name: '対処すべき課題', path: '' },
  ]

  return (
    <>
      <Head
        title="対処すべき課題"
        description="トライアルホールディングスの優先的に対処すべき事業上の課題について掲載しています。"
        ogp={{ url: 'ir/manage/issue/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}
